import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect
} from 'react';
import { useFilters, FiltersOptions } from 'apiTyped/hooks/negoce.query';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Campaign {
  id: number;
  name: string;
}

interface Wine {
  id: number;
  name: string;
}

interface SaleType {
  id: number;
  name: string;
}

interface Negociant {
  id: number;
  name: string;
}

interface NegoceFiltersState {
  campaignId: number | undefined;
  wineId: number | undefined;
  saleTypeId: number | undefined;
  negociantId: number | undefined;
  campaigns: Campaign[];
  wines: Wine[];
  saleTypes: SaleType[];
  negociants: Negociant[];
  isLoading: boolean;
  setCampaignId: (id: number | undefined) => void;
  setWineId: (id: number | undefined) => void;
  setSaleTypeId: (id: number | undefined) => void;
  setNegociantId: (id: number | undefined) => void;
}

const NegoceFiltersContext = createContext<NegoceFiltersState | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

export const NegoceFiltersProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [campaignId, setCampaignId] = useState<number | undefined>(undefined);
  const [wineId, setWineId] = useState<number | undefined>(undefined);
  const [saleTypeId, setSaleTypeId] = useState<number | undefined>(undefined);
  const [negociantId, setNegociantId] = useState<number | undefined>(undefined);

  const { isLoadingFilters, filters } = useFilters({ campaignId });

  // Auto-select first campaign when filters load
  useEffect(() => {
    if (!campaignId && filters.campaigns.length > 0) {
      setCampaignId((filters.campaigns[0] as any).id);
    }
  }, [filters.campaigns, campaignId]);

  const value: NegoceFiltersState = {
    campaignId,
    wineId,
    saleTypeId,
    negociantId,
    campaigns: filters.campaigns as any[],
    wines: filters.wines as any[],
    saleTypes: filters.saleTypes as any[],
    negociants: filters.negociants as any[],
    isLoading: isLoadingFilters,
    setCampaignId,
    setWineId,
    setSaleTypeId,
    setNegociantId
  };

  return (
    <NegoceFiltersContext.Provider value={value}>
      {children}
    </NegoceFiltersContext.Provider>
  );
};

// ─── Hook ───────────────────────────────────────────────────────────────────

export const useNegoceFilters = (): NegoceFiltersState => {
  const ctx = useContext(NegoceFiltersContext);
  if (!ctx)
    throw new Error(
      'useNegoceFilters must be used within NegoceFiltersProvider'
    );
  return ctx;
};
